"use client";

import React, { useEffect } from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Loader, ArrowRight, Users, Clock, MapPin } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function SendAMessage() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="223915" id="segíthetünk" ref={sectionRef} className="bg-background text-foreground py-24 px-4 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none opacity-20">
        <div className="absolute top-[-10%] right-[-5%] w-[40%] h-[40%] bg-primary/10 blur-[120px] rounded-full" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[30%] h-[30%] bg-primary/5 blur-[100px] rounded-full" />
      </div>

      <div className="max-w-6xl mx-auto relative z-10">
        <div className="text-center mb-16 space-y-4">
          <h2 className={`text-4xl md:text-6xl font-default font-bold tracking-tighter transition-all duration-700 transform ${isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
            Kérdése van? <span className="text-primary">Írjon nekünk</span>
          </h2>
          <p className={`text-muted-foreground font-default max-w-2xl mx-auto transition-all duration-700 delay-100 transform ${isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
            Szakértőink segítenek a tökéletes utazás, szállás vagy repülőjegy kiválasztásában.
          </p>
        </div>

        <div className="grid lg:grid-cols-[1fr_350px] gap-12 items-start">
          <div className={`transition-all duration-700 delay-200 transform ${isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
            <FormProvider formKey="help_form_contact" formId="help_form_contact" sectionName="Send a Message" defaultValues={{
            name: "",
            email: "",
            phone: "",
            subject: "",
            message: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            message: {
              required: true
            }
          }} submitText="Üzenet küldése" successMessage="Köszönjük! Üzenetét megkaptuk, hamarosan válaszolunk.">
              <ContactFormInner />
            </FormProvider>
          </div>

          <div className={`space-y-6 transition-all duration-700 delay-300 transform ${isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
            <Card className="bg-card/80 backdrop-blur-md border-border rounded-lg shadow-xl relative overflow-hidden group">
              <div className="absolute top-0 left-0 w-1 h-full bg-primary transform origin-bottom scale-y-0 group-hover:scale-y-100 transition-transform duration-300" />
              <CardHeader>
                <CardTitle className="font-default text-xl tracking-tight flex items-center gap-2">
                  <Users className="size-5 text-primary" />
                  Csoportos foglalás
                </CardTitle>
                <CardDescription className="font-default text-sm">
                  Többen utaznának együtt?
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 font-default text-sm text-muted-foreground">
                <p>
                  Legyen szó céges útról vagy családi nyaralásról, egyedi ajánlatokkal és taxi transzferrel segítjük a szervezést.
                </p>
                <ul className="space-y-3 pt-4 border-t border-border/50">
                  <li className="flex items-start gap-3">
                    <Clock className="size-4 text-primary shrink-0 mt-0.5" />
                    <span>Gyors válaszadás minden megkeresésre 24 órán belül.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <MapPin className="size-4 text-primary shrink-0 mt-0.5" />
                    <span>Globális lefedettség, helyi szakértelemmel.</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      <Toaster />
    </section>;
}
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  useEffect(() => {
    if (isSubmitted && !submitError) {
      toast.success(successMessage);
    } else if (submitError) {
      toast.error(submitError);
    }
  }, [isSubmitted, submitError, successMessage]);
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-card border border-border shadow-2xl rounded-lg flex flex-col items-center text-center space-y-4 animate-in fade-in duration-500">
        <div className="size-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
          <div className="size-8 bg-primary rounded-full animate-pulse" />
        </div>
        <h3 className="font-default text-2xl font-bold">Üzenet elküldve</h3>
        <p className="text-muted-foreground font-default">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 rounded-full font-default tracking-wide border-primary text-primary hover:bg-primary hover:text-primary-foreground">
          Új üzenet küldése
        </Button>
      </div>;
  }
  const inputClasses = "flex w-full bg-transparent border-0 border-b border-border px-0 py-3 text-base font-default text-foreground placeholder:text-muted-foreground/40 focus-visible:outline-none focus-visible:border-b-2 focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 transition-all rounded-none";
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="name" rules={{
        required: "A név megadása kötelező"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-xs uppercase tracking-widest text-muted-foreground">
                Az Ön neve *
              </FormLabel>
              <FormControl>
                <input type="text" placeholder="Kovács János" className={inputClasses} {...field} />
              </FormControl>
              <FormMessage className="font-default text-xs" />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Az e-mail cím megadása kötelező",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Érvénytelen e-mail cím"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-xs uppercase tracking-widest text-muted-foreground">
                E-mail cím *
              </FormLabel>
              <FormControl>
                <input type="email" placeholder="janos@pelda.hu" className={inputClasses} {...field} />
              </FormControl>
              <FormMessage className="font-default text-xs" />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-xs uppercase tracking-widest text-muted-foreground">
                Telefonszám
              </FormLabel>
              <FormControl>
                <input type="tel" placeholder="+36 30 123 4567" className={inputClasses} {...field} />
              </FormControl>
              <FormMessage className="font-default text-xs" />
            </FormItem>} />

        <FormField control={control} name="subject" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-xs uppercase tracking-widest text-muted-foreground">
                Tárgy
              </FormLabel>
              <FormControl>
                <input type="text" placeholder="pl. Szállásfoglalás érdeklődés" className={inputClasses} {...field} />
              </FormControl>
              <FormMessage className="font-default text-xs" />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Az üzenet megadása kötelező"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-xs uppercase tracking-widest text-muted-foreground">
              Üzenet *
            </FormLabel>
            <FormControl>
              <textarea placeholder="Írja le kérdését részletesen..." className={`${inputClasses} min-h-[120px] resize-y`} {...field} />
            </FormControl>
            <FormMessage className="font-default text-xs" />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 text-destructive font-default text-sm rounded-lg">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-16 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-default text-lg tracking-wide font-bold transition-all hover:translate-y-[-2px] hover:shadow-xl">
        {isSubmitting ? <>
            <Loader className="mr-2 size-5 animate-spin" />
            Küldés folyamatban...
          </> : <>
            Üzenet elküldése
            <ArrowRight className="ml-2 size-5" />
          </>}
      </Button>
    </Form>;
}